import { useEffect, useId, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  ConfirmDialog,
  Drawer,
  DrawerFooter,
  ErrorBanner,
  LoadingButton,
  useToast,
  type ImpactItem,
} from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { errorView, fieldMessage } from '@app/errors';
import { t, tn } from '@app/i18n';
import { TAB_PARAM } from '@app/navigation';
import { useCanOperar } from '@app/auth/useCan';
import { useCurrentUser } from '@app/auth/useCurrentUser';
import { readingView, useClusterReading, type ReadingState } from '@app/reading';
import { userOwner, useUsersForOwner } from '@app/shared/owner';
import { IdentityForm } from './IdentityForm';
import { IdentityStateBadge } from './IdentityStateBadge';
import { CapabilitiesTab } from './IdentityDrawer/CapabilitiesTab';
import { PoliciesTab } from './IdentityDrawer/PoliciesTab';
import { ReleasesTab } from './IdentityDrawer/ReleasesTab';
import { SpiffeTab } from './IdentityDrawer/SpiffeTab';
import { ReceivesA2A } from './IdentityDrawer/ReceivesA2A';
import {
  identitiesKeys,
  useAssignIdentity,
  useCreateIdentityRole,
  useDeleteIdentity,
  useIdentity,
  useIdentityImpact,
  useIdentityRoles,
  useInfraConfig,
  useSpiffePreview,
} from './Identities.api';
import {
  identityDefaults,
  forAssignment,
  registryExpected,
  type IdentityFormValues,
} from './IdentityModel';
import { useIdentityValidation } from './useFormValidation';
import { useEnvironmentsSelectable } from '../TenantSettings/TenantSettings.api';
import s from './Identities.module.css';

/**
 * T5 — drawer da identidade (FR-045 a FR-049, FR-054, FR-056).
 *
 * Cinco abas, como o protótipo (§3.1): Identidade (a atribuição), SPIFFE / SPIRE,
 * Capabilities, Políticas e Releases. Cada aba carrega só quando abre e tem o
 * seu próprio carregando/erro (FR-126) — uma leitura degradada do middleware não
 * derruba as outras.
 */

/**
 * Só os **ids** são constante de módulo; o rótulo sai do catálogo no render
 * (`tabLabel`). Uma lista com `label` pronto seria montada na importação e
 * seguiria mostrando o idioma de então depois de uma troca.
 */
const ABAS = ['identity', 'spiffe', 'capabilities', 'policies', 'releases'] as const;

type TabID = (typeof ABAS)[number];

function tabLabel(id: TabID): string {
  return t(`identities.drawer.tab.${id}`);
}

/** A aba que o endereço pede (`identityDrawerHref`, aim-ypw), ou a primeira. */
function tabOf(value: string | null): TabID {
  return (ABAS as readonly string[]).includes(value ?? '') ? (value as TabID) : 'identity';
}

/**
 * Frase dos pods no diálogo de remoção — **três estados, três frases**.
 *
 * `pods_em_execucao` é nulável de propósito no contrato: `null` é "o cluster não
 * respondeu", e nunca `0`, porque numa confirmação destrutiva "não há pod" e "não
 * sei" levam a decisões diferentes. A tela testava o valor por *truthiness*, e em
 * JavaScript `0` e `null` são os dois falsos — as duas respostas desenhavam a
 * mesma coisa (nada), apagando no último metro a distinção que o backend
 * construiu. Quem lesse concluía "nenhum pod afetado" nos dois casos.
 */
function phrasePods(pods: number | null | undefined, podsReading?: ReadingState): string {
  if (pods == null) return t('identities.drawer.pods.unknown');
  // A hora da foto do cluster de onde o número sai (aim-6ph.13, §3.3): «(lido às 14:05)». Com número, há foto, e a
  // frase sempre tem a hora. O diálogo não dispara leitura (decisão 2): ele lê a foto que a gaveta renovou ao abrir.
  const reading = readingView(podsReading)?.text ?? '';
  if (pods === 0) return t('identities.drawer.pods.none', { reading });
  return tn('identities.drawer.pods.running', pods, { reading });
}

export interface IdentityDrawerProps {
  identityId: string;
  onClose: () => void;
}

export function IdentityDrawer({ identityId, onClose }: IdentityDrawerProps) {
  const identidade = useIdentity(identityId);
  // Abrir a gaveta pede a leitura da identidade (decisão 2): o L7 da aplicação, o L10 e o L9, uma vez por abertura.
  // As abas mostram a foto com a hora e se atualizam sozinhas quando ela termina. Sem slug não há o que ler.
  const reading = useClusterReading({
    scope: 'identity',
    identityId,
    enabled: Boolean(identidade.data?.slug),
    refresh: [
      identitiesKeys.item(identityId),
      identitiesKeys.spiffe(identityId),
      identitiesKeys.capabilities(identityId),
      identitiesKeys.impact(identityId),
    ],
  });
  const roleList = useIdentityRoles();
  const infra = useInfraConfig();

  const canSave = useCanOperar('assignIdentity');
  const canRemove = useCanOperar('deleteIdentity');
  const toast = useToast();

  // Opções do campo **Dono**. A condição é `listUsers` (admin), **não**
  // `podeSalvar`: `assignIdentity` também é de approver, e um approver que
  // disparasse esta consulta tomaria 403 ao abrir o drawer. Sem a lista o
  // combobox ainda oferece o «(você)» e o valor gravado.
  const canListUsers = useCanOperar('listUsers');
  const usuarios = useUsersForOwner(canListUsers && canSave);
  const { usuario } = useCurrentUser();
  // Identidade sem dono abre com quem está logado — quem atribui costuma ser
  // quem assume o agente. O dono já gravado sempre vence este padrão.
  const ownerDefault = usuario ? userOwner(usuario) : '';

  const registryPrefix = registryExpected(infra.data);
  // O CADASTRO DE AMBIENTES ALIMENTA O COMBOBOX **E** O SCHEMA (Lote 3 · F1-c)
  //
  // Uma leitura, dois consumidores, e é isso que importa: as opções oferecidas e os
  // valores aceitos pela validação saem da MESMA lista. Duas fontes aqui fariam o
  // formulário oferecer um ambiente que ele próprio recusa no `submit` — que é o
  // defeito mais irritante possível, porque a pessoa escolheu o que a tela mostrou.
  //
  // `Selectable` e não a lista completa: uma linha do eixo C (metadado do catálogo
  // do executor) nunca é ambiente de um agente.
  const environments = useEnvironmentsSelectable();
  const environmentSlugs = useMemo(
    () => (environments.data ?? []).map((e) => e.slug),
    [environments.data],
  );
  const environmentOptions = useMemo(
    () =>
      (environments.data ?? []).map((e) => ({
        value: e.slug,
        // `name` primeiro (é o que a pessoa reconhece), slug ao lado (é o que ela
        // vai ver gravado na linha e no namespace). Sem o slug, escolher «Produção»
        // e ver `prod` na tabela obriga a saber o mapeamento de cabeça.
        label: e.name && e.name !== e.slug ? `${e.name} (${e.slug})` : e.slug,
      })),
    [environments.data],
  );

  const validation = useIdentityValidation(registryPrefix, environmentSlugs);

  const { control, handleSubmit, reset, setValue, setError, watch, formState } =
    useForm<IdentityFormValues>({
      defaultValues: identityDefaults(identidade.data, ownerDefault),
      ...validation,
    });

  const assign = useAssignIdentity(identityId);
  const createRole = useCreateIdentityRole();
  const remove = useDeleteIdentity(identityId);

  const [searchParams, setSearchParams] = useSearchParams();
  // aim-ypw: o link «Ver na aba Capabilities» do Security Ready abre a gaveta na aba. O `tab` é lido ao abrir e sai do
  // endereço logo depois, para a próxima gaveta não herdar a aba.
  const [tab, setAba] = useState<TabID>(() => tabOf(searchParams.get(TAB_PARAM)));
  useEffect(() => {
    if (!searchParams.has(TAB_PARAM)) return;
    const next = new URLSearchParams(searchParams);
    next.delete(TAB_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  /**
   * `role="tab"` sem `role="tabpanel"` é uma promessa quebrada: o leitor de tela
   * anuncia "aba 1 de 5" e não tem para onde ir. O `useId` dá o prefixo porque
   * dois drawers na mesma página duplicariam os ids.
   */
  const idBase = useId();
  const tabID = (id: TabID) => `${idBase}-aba-${id}`;
  const idDoPainel = (id: TabID) => `${idBase}-painel-${id}`;
  const [erroSalvar, setErroSalvar] = useState<ApiError | null>(null);
  const [erroRemover, setErroRemover] = useState<ApiError | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const impacto = useIdentityImpact(identityId, confirmando);

  /**
   * A identidade chega depois do primeiro render: o formulário nasce com os
   * valores do servidor (imagem pré-preenchida pelo discovery — FR-045).
   *
   * `formPronto` existe por causa de UM render (Lote 3 · F0-b). A sequência era:
   *
   *  1. `useForm` acima é avaliado ANTES de a identidade chegar, então nasce com
   *     os defaults vazios;
   *  2. assim que `identidade.data` existe, o `IdentityForm` já renderiza;
   *  3. só então este efeito roda o `reset` e enche o formulário.
   *
   * Entre 2 e 3 há um render em que o formulário está montado com valores VAZIOS
   * sobre uma identidade que tem valores. Nele o namespace na tela é `''` e o
   * gravado é `agentes`, então tudo que compara «tela × gravado» conclui
   * «editado» — e a tela pisca a prévia de um formulário que ninguém preencheu.
   *
   * O `act` do Testing Library normalmente descarrega o efeito no mesmo bloco,
   * por isso a janela quase nunca aparecia; com a máquina saturada, a ordem entre
   * o efeito e a sondagem do teste deixava de ser sempre a mesma, e o vermelho
   * saía num `getByText` síncrono sobre o texto de origem do ambiente.
   *
   * A correção é de produto, não de asserção: **o formulário não monta antes de
   * estar preenchido**. Isso fecha a classe inteira — a prévia do SPIFFE, o
   * ambiente efetivo e qualquer comparação futura entre tela e gravado —, e não
   * só o sintoma que apareceu.
   */
  const [formPronto, setFormPronto] = useState(false);
  useEffect(() => {
    if (!identidade.data) return;
    reset(identityDefaults(identidade.data, ownerDefault));
    setFormPronto(true);
  }, [identidade.data, ownerDefault, reset]);

  const namespace = watch('namespace');
  const workload = watch('workload');
  const serviceAccount = watch('service_account');
  // A prévia do SPIFFE ID é do SERVIDOR (aim-xsk.7): o template do site compõe, a
  // tela só mostra. Sem edição vale o `spiffe_id_predicted` que já veio no
  // `getIdentity`; editado, pergunta-se o que a tela daria. Até aqui a tela compunha
  // `/ns/<ns>/sa/<sa>` à mão e divergia do servidor em site novo.
  //
  // O preenchimento automático «service account = slug» (ajustes-front #6) saiu: no
  // modo workload o valor certo é o controller do cluster, que chega pela sugestão.
  const storedNamespace = identidade.data?.namespace ?? '';
  const storedWorkload = identidade.data?.workload ?? '';
  const storedServiceAccount = identidade.data?.service_account ?? '';
  const spiffeEdited =
    formPronto &&
    (namespace.trim() !== storedNamespace ||
      workload.trim() !== storedWorkload ||
      serviceAccount.trim() !== storedServiceAccount);
  // A prévia corre também SEM edição (02/10): é ela que diz o template do site, e o
  // template decide se a Service Account é obrigatória e se congela.
  const spiffePreview = useSpiffePreview(
    identidade.data?.id ?? null,
    { namespace: namespace.trim(), workload: workload.trim(), serviceAccount: serviceAccount.trim() },
    formPronto,
  );
  const serviceAccountInTemplate = (spiffePreview.data?.template ?? '').includes('{service_account}');
  const spiffeExpected = spiffeEdited
    ? (spiffePreview.data?.spiffe_id ?? null)
    : (identidade.data?.spiffe_id_predicted ?? null);

  const save = handleSubmit(async (valores) => {
    if (!identidade.data) return;
    setErroSalvar(null);
    try {
      const saves = await assign.mutateAsync(forAssignment(valores, identidade.data.version));
      reset(identityDefaults(saves, ownerDefault));
      // `slug` é nulável no contrato: com o template literal anterior, uma
      // identidade sem slug produzia o toast «Atribuição de null salva». Só a
      // interpolação nomeada tornou isso visível (o marcador exige um valor).
      toast.mostrar(
        t('identities.drawer.saved', {
          name: saves.slug ?? t('identities.drawer.fallbackName'),
        }),
        { tone: 'sucesso' },
      );
    } catch (erro) {
      const apiError = erro as ApiError;
      setErroSalvar(apiError);
      // 422 por campo vira erro inline no próprio campo (item 5.3 do PO, 28/08:
      // «já existe uma identidade com este SPIFFE ID: {slug}» em namespace e
      // service account) — o banner continua listando; o campo é onde se corrige.
      for (const campo of apiError.campos) {
        if (campo.field in valores) {
          setError(campo.field as keyof IdentityFormValues, {
            type: 'server',
            message: fieldMessage(campo),
          });
        }
      }
      // `versao_antiga` descarta a tentativa e recarrega o registro (FR-124);
      // os demais códigos mantêm o que foi digitado (FR-123).
      if (errorView(apiError).efeito === 'recarregar') {
        const current = await identidade.refetch();
        if (current.data) reset(identityDefaults(current.data, ownerDefault));
      }
    }
  });

  async function confirmRemoval() {
    setErroRemover(null);
    try {
      await remove.mutateAsync();
      const nome = identidade.data?.slug ?? t('identities.drawer.fallbackName');
      setConfirmando(false);
      toast.mostrar(t('identities.drawer.removed', { name: nome }), { tone: 'sucesso' });
      onClose();
    } catch (erro) {
      setErroRemover(erro as ApiError);
    }
  }

  const nome =
    identidade.data?.slug ??
    identidade.data?.image_basename ??
    t('identities.drawer.fallbackName');
  const impactItems: ImpactItem[] = [
    { label: t('identities.drawer.impact.policies'), count: impacto.data?.policies ?? 0 },
    {
      label: t('identities.drawer.impact.a2a'),
      count: impacto.data?.a2a_references ?? 0,
    },
    { label: t('identities.drawer.impact.pending'), count: impacto.data?.pending_items ?? 0 },
  ];

  return (
    <Drawer
      open
      title={nome}
      subtitle={
        identidade.data ? <IdentityStateBadge state={identidade.data.state} tip={false} /> : null
      }
      dirty={formState.isDirty}
      onClose={onClose}
      footer={
        identidade.data && tab === 'identity' ? (
          <DrawerFooter
            start={
              <>
                {/* Rodapé de T5 (PRD §4): Ver aprovação pendente · Simular · Contrato · Remover. */}
                {identidade.data.state === 'nova_aguardando_aprovacao' ||
                identidade.data.state === 'alterada_reaprovacao_pendente' ? (
                  <Link
                    className={s.linkRodape}
                    to={`/aprovacoes?identidade=${identityId}`}
                  >
                    {t('identities.drawer.footer.pendingApproval')}
                  </Link>
                ) : null}
                <Link className={s.linkRodape} to="/simulador">
                  {t('identities.drawer.footer.simulate')}
                </Link>
                <Link className={s.linkRodape} to={`/contratos?identidade=${identityId}`}>
                  {t('identities.drawer.footer.contract')}
                </Link>
                {canRemove ? (
                  <Button variant="danger" icon="trash" onClick={() => setConfirmando(true)}>
                    {t('identities.drawer.footer.remove')}
                  </Button>
                ) : null}
              </>
            }
          >
            <Button variant="out" onClick={onClose}>
              {t('identities.drawer.footer.cancel')}
            </Button>
            {canSave ? (
              <LoadingButton
                variant="primary"
                loading={assign.isPending}
                loadingLabel={t('identities.drawer.footer.save.loading')}
                onClick={() => save()}
              >
                {t('identities.drawer.footer.save')}
              </LoadingButton>
            ) : null}
          </DrawerFooter>
        ) : null
      }
    >
      {identidade.isLoading ? (
        <p className={s.carregando}>{t('identities.drawer.loading')}</p>
      ) : null}

      {identidade.isError ? (
        <ErrorBanner
          error={identidade.error}
          title={t('identities.drawer.error.open')}
          onRetry={() => void identidade.refetch()}
          hideLink
        />
      ) : null}

      {erroSalvar ? (
        <ErrorBanner
          error={erroSalvar}
          escrita
          title={t('identities.drawer.error.save')}
          onRetry={() => setErroSalvar(null)}
          retryLabel={t('identities.drawer.error.save.close')}
        >
          {erroSalvar.campos.length ? (
            <ul>
              {erroSalvar.campos.map((campo) => (
                <li key={campo.field}>
                  <strong>{campo.field}</strong>: {fieldMessage(campo)}
                </li>
              ))}
            </ul>
          ) : null}
        </ErrorBanner>
      ) : null}

      {identidade.data ? (
        <div className={s.abas} role="tablist" aria-label={t('identities.drawer.tabs.label')}>
          {ABAS.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={tabID(id)}
              aria-selected={tab === id}
              aria-controls={idDoPainel(id)}
              className={[s.aba, tab === id ? s.abaOn : ''].filter(Boolean).join(' ')}
              onClick={() => setAba(id)}
            >
              {tabLabel(id)}
            </button>
          ))}
        </div>
      ) : null}

      {/* Um painel por vez: só a aba selecionada é montada, então o `id` do painel
          é o da aba corrente e o `aria-controls` de cada botão sempre aponta para
          o painel que aquele botão abre. `tabIndex={0}` porque o conteúdo pode não
          ter foco próprio (uma tabela, um banner) e sem ele o teclado passa das
          abas direto para o rodapé. */}
      {identidade.data ? (
        <div
          role="tabpanel"
          id={idDoPainel(tab)}
          aria-labelledby={tabID(tab)}
          tabIndex={0}
          className={s.painel}
        >
          {identidade.data && tab === 'spiffe' ? (
            <SpiffeTab
              identityId={identityId}
              ativa={tab === 'spiffe'}
              readingPending={reading.pending}
            />
          ) : null}
          {identidade.data && tab === 'capabilities' ? (
            <CapabilitiesTab
              identityId={identityId}
              ativa={tab === 'capabilities'}
              readingPending={reading.pending}
            />
          ) : null}
          {identidade.data && tab === 'policies' ? (
            <PoliciesTab identityId={identityId} ativa={tab === 'policies'} />
          ) : null}
          {identidade.data && tab === 'releases' ? (
            <ReleasesTab identityId={identityId} ativa={tab === 'releases'} />
          ) : null}

          {/* `formPronto`: o formulário não monta antes do `reset` enchê-lo — ver
              o comentário do efeito acima. Sem isso há um render com os campos
              vazios sobre uma identidade que tem valores. */}
          {identidade.data && formPronto && tab === 'identity' ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <IdentityForm
                identidade={identidade.data}
                control={control}
                errors={formState.errors}
                papeis={roleList.data ?? []}
                usuarios={usuarios.data ?? []}
                spiffePrevisto={spiffeExpected}
                serviceAccountInTemplate={serviceAccountInTemplate}
                // O ambiente é derivado **pelo servidor**, ao salvar; enquanto o
                // namespace digitado difere do gravado, o formulário pede a prévia
                // ao servidor (`getIdentityEnvironmentPreview`, Lote 3 · F0-b) —
                // antes disso ela era calculada no cliente, com uma cópia da regra
                // que só alcançava dois dos quatro elos da D-6.
                namespaceDigitado={namespace}
                prefixoDoRegistry={registryPrefix}
                environmentOptions={environmentOptions}
                // Sem `assignIdentity` não há como gravar: o formulário abre em modo
                // leitura em vez de convidar a digitar o que não vai a lugar nenhum.
                somenteLeitura={!canSave}
                criandoPapel={createRole.isPending}
                onCriarPapel={(nomeDoPapel) => {
                  createRole.mutate(nomeDoPapel, {
                    onSuccess: (papel) => {
                      // O `reset` com `keepValues: true` mantinha os valores atuais e
                      // ignorava o papel novo: o combobox voltava vazio e o Salvar
                      // seguinte levava 422 «papel: Informe o papel» (E2E 4.6/4.8).
                      setValue('papel', papel.name, { shouldDirty: true, shouldValidate: true });
                      toast.mostrar(
                        t('identities.drawer.roleCreated', { role: papel.name, name: nome }),
                        { tone: 'sucesso' },
                      );
                    },
                    onError: (erro) => setErroSalvar(erro),
                  });
                }}
              />
              {/* Fase 2: «Recebe A2A» — só quando um recurso agente está ligado a esta identidade. */}
              <ReceivesA2A identityId={identityId} agents={identidade.data.agent_resources ?? []} />
            </form>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmando}
        title={t('identities.drawer.remove.title', { name: nome })}
        destructive
        confirmLabel={t('identities.drawer.remove.confirm')}
        loading={remove.isPending || impacto.isLoading}
        message={
          impacto.data ? (
            <>
              <p>
                {/* Duas formas de plural, do `Intl.PluralRules` — o «regra(s)» que
                    estava aqui é o que o glossário da auditoria de copy proíbe. */}
                {t(
                  impacto.data.active_contract
                    ? 'identities.drawer.remove.withContract'
                    : 'identities.drawer.remove.withoutContract',
                  {
                    version: impacto.data.contract_version ?? '?',
                    policies: tn('identities.drawer.remove.reachedBy', impacto.data.policies),
                    a2a: tn('identities.drawer.remove.invokedBy', impacto.data.a2a_references),
                  },
                )}
              </p>
              {identidade.data?.agent_resources?.length ? (
                // Decisão 23: o recurso agente fica, sem destino confiável, e quem chamava guarda a delegação.
                <p>
                  {t('identities.drawer.remove.agents', {
                    names: identidade.data.agent_resources.map((agent) => agent.name).join(', '),
                  })}
                </p>
              ) : null}
              <p>
                {t('identities.drawer.remove.note')}{' '}
                <span data-testid="pods-reading" data-state={readingView(impacto.data.pods_reading)?.state}>
                  {phrasePods(impacto.data.running_pods, impacto.data.pods_reading)}
                </span>
              </p>
            </>
          ) : impacto.isError ? (
            // Sem impacto lido não há confirmação informada — o FR-056 manda o
            // diálogo *listar* o impacto (contrato, políticas, A2A). O ramo antes
            // era binário (`data ? … : "Lendo…"`), então qualquer erro deixava a
            // frase de carregando na tela para sempre, com o botão destrutivo
            // habilitado: a pessoa confirmava às cegas. Não há "remover mesmo
            // assim" — a saída é ler o impacto ou cancelar.
            <p>{t('identities.drawer.remove.impactRetry')}</p>
          ) : (
            <p>{t('identities.drawer.remove.impactLoading')}</p>
          )
        }
        impact={impacto.data ? impactItems : undefined}
        confirmDisabled={!impacto.data}
        error={
          erroRemover ? (
            <ErrorBanner
              error={erroRemover}
              escrita
              title={t('identities.drawer.remove.error')}
            />
          ) : impacto.isError ? (
            <ErrorBanner
              error={impacto.error}
              title={t('identities.drawer.remove.impactError')}
              onRetry={() => void impacto.refetch()}
              hideLink
            />
          ) : null
        }
        onConfirm={confirmRemoval}
        onCancel={() => {
          setConfirmando(false);
          setErroRemover(null);
        }}
      />
    </Drawer>
  );
}
